import type { KeyboardEvent } from 'react';
import { YEAR_PAGE_SIZE } from './UiDatePicker.constants';
import type { CalendarGridProps } from './UiDatePicker.types';
import { chunk, clampToRange, fromIso, isYearInRange, withYear, yearPageStart } from './UiDatePicker.utils';
import { UiDatePickerCell } from './UiDatePickerCell';

type UiDatePickerYearsProps = CalendarGridProps & {
    /** Picks the year; the calendar returns to its months. */
    onSelect: (iso: string) => void;
};

/** Grid keys move a year sideways, a row up or down and a page with Page Up / Down. */
const YEAR_STEPS: Record<string, number> = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -3,
    ArrowDown: 3,
    PageUp: -YEAR_PAGE_SIZE,
    PageDown: YEAR_PAGE_SIZE,
};

/** A page of twelve years around the focused one. */
export function UiDatePickerYears({ focus, value, range, onFocusChange, onSelect }: UiDatePickerYearsProps) {
    const year = fromIso(focus).getFullYear();
    const start = yearPageStart(year);
    const selectedYear = value ? fromIso(value).getFullYear() : null;
    const thisYear = new Date().getFullYear();
    const rows = chunk(
        Array.from({ length: YEAR_PAGE_SIZE }, (_, index) => start + index),
        3,
    );

    const handleKeyDown = (event: KeyboardEvent) => {
        const step = YEAR_STEPS[event.key];
        if (step === undefined) return;

        event.preventDefault();
        onFocusChange(clampToRange(withYear(focus, year + step), range), true);
    };

    return (
        <div
            role="grid"
            aria-label={`${start} – ${start + YEAR_PAGE_SIZE - 1}`}
            className="form-datepicker-grid"
            data-columns="3"
            onKeyDown={handleKeyDown}
        >
            {rows.map((row) => (
                <div key={row[0]} role="row" className="form-datepicker-row">
                    {row.map((cellYear) => (
                        <span key={cellYear} role="gridcell">
                            <UiDatePickerCell
                                label={String(cellYear)}
                                focused={cellYear === year}
                                selected={cellYear === selectedYear}
                                current={cellYear === thisYear}
                                disabled={!isYearInRange(cellYear, range)}
                                onClick={() => onSelect(clampToRange(withYear(focus, cellYear), range))}
                            >
                                {cellYear}
                            </UiDatePickerCell>
                        </span>
                    ))}
                </div>
            ))}
        </div>
    );
}
